import logger from 'ws-scripts/modules/logger';
import { PAYMENT_TYPES } from '../../../constants';
import {
	PAYMENT_TYPE_CONFIG,
	DEAL_TYPE_CONFIG,
	DEFAULT_DISPLAY_INDEX,
	NATIONAL_AVERAGE_RATE
} from '../constants';
import {
	isValidPaymentObject,
	validateAndNormalizePaymentArray
} from '../validation';
import {
	capitalizeFirst,
	sortByDisplayIndex
} from '../utils/transformation';

export const hasAnyValidPaymentInRawData = (paymentArray) => {
	const payments = Array.isArray(paymentArray?.[0])
		? paymentArray[0]
		: paymentArray;
	if (!Array.isArray(payments) || payments.length === 0) return false;
	return payments.some((p) => p?.payment > 0);
};

export const processPaymentObjectUpdate = (paymentArray = [], enableIncentives, flags = {}) => {
	const validation = validateAndNormalizePaymentArray(paymentArray);

	if (!validation.valid) {
		logger.error('WS-TRACK-MY-OFFER: Invalid PaymentObject data structure', {
			error: validation.error,
			receivedType: typeof paymentArray,
			isArray: Array.isArray(paymentArray)
		});
		return null;
	}

	const payments = validation.normalized;

	logger.info('WS-TRACK-MY-OFFER: Processing PaymentObject:Update data', {
		paymentArray,
		paymentCount: payments.length,
		paymentTypes: payments.map((p) => p.paymentLabel || 'unknown')
	});

	const formattedPayments = payments
		.filter(isValidPaymentObject)
		.map((paymentObj) => {
			const {
				paymentLabel,
				payment,
				isPaymentError = false,
				...otherProps
			} = paymentObj;
			let resolvedLabel = paymentLabel;
			let reportedError = isPaymentError;

			if (!resolvedLabel) {
				if (
					typeof payment === 'number' &&
					payment > 0 &&
					!otherProps.loanTerm &&
					!otherProps.leaseTerm
				) {
					resolvedLabel = PAYMENT_TYPES.CASH;
					reportedError = false;
				} else if (otherProps.loanTerm || otherProps.interestRate) {
					resolvedLabel = PAYMENT_TYPES.FINANCE;
				} else if (otherProps.leaseTerm || otherProps.annualMiles) {
					resolvedLabel = PAYMENT_TYPES.LEASE;
				} else {
					logger.warn(
						'WS-TRACK-MY-OFFER: Unable to determine payment type from PaymentObject',
						{ paymentObj }
					);
					return null;
				}
			}

			const isCashType =
				resolvedLabel === PAYMENT_TYPES.CASH ||
				(resolvedLabel && resolvedLabel.toLowerCase() === 'cash');

			// For cash, prefer cashAmount or salePrice over payment
			// payment field for cash includes taxes/fees, but widget should display sale price
			const displayPayment = isCashType
				? (otherProps.cashAmount
					|| otherProps.pricingDetails?.salePrice
					|| payment)
				: payment;

			const isNationalAverageRate = otherProps.lender === NATIONAL_AVERAGE_RATE;
			const shouldGateNationalAverageRate = flags['ws-track-my-offer-test-future'] === true;

			const hasPaymentError =
				((typeof displayPayment !== 'number' || Number.isNaN(displayPayment) || displayPayment <= 0) && reportedError !== false) 
				|| (shouldGateNationalAverageRate && isNationalAverageRate);

			const capitalizedLabel = capitalizeFirst(resolvedLabel);
			const configKey =
				DEAL_TYPE_CONFIG[resolvedLabel] ||
				`${resolvedLabel}Offers`;
			const displayIndex =
				PAYMENT_TYPE_CONFIG[configKey]?.displayIndex ??
				DEFAULT_DISPLAY_INDEX;

			const processedPayment = {
				payment: hasPaymentError ? 0 : Math.floor(displayPayment),
				paymentTerm:
					otherProps.loanTerm || otherProps.leaseTerm || null,
				paymentLabel: capitalizedLabel,
				hasError: hasPaymentError,
				hasSpecial:
					enableIncentives === 'true' &&
					Array.isArray(otherProps.incentiveIds) &&
					otherProps.incentiveIds.length > 0,
				incentiveIds: otherProps.incentiveIds || [],
				key: `paymentOption_${resolvedLabel}Offers`,
				displayIndex,
				downPayment: otherProps.downPayment,
				apr: otherProps.interestRate,
				annualMiles: otherProps.annualMiles,
				hasTaxesAndFees: !!(
					otherProps.taxesAndFees || otherProps.taxesFees
				),
				isSelected: false
			};

			logger.info(
				`WS-TRACK-MY-OFFER: Processed ${resolvedLabel} payment`,
				{
					original: paymentObj,
					processed: processedPayment,
					hasError: processedPayment.hasError,
					payment: processedPayment.payment
				}
			);

			return processedPayment;
		})
		.filter((payment) => payment !== null)
		.sort(sortByDisplayIndex);

	const validPayments = formattedPayments.filter(
		(payment) => !payment.hasError
	);
	const errorPayments = formattedPayments.filter(
		(payment) => payment.hasError
	);
	const hasValidPayments = validPayments.some(
		(payment) => payment.payment > 0
	);

	logger.info('WS-TRACK-MY-OFFER: PaymentObject validation results', {
		totalPayments: formattedPayments.length,
		validPayments: validPayments.length,
		errorPayments: errorPayments.length,
		hasValidPayments,
		validPaymentTypes: validPayments.map((p) => p.paymentLabel),
		errorPaymentTypes: errorPayments.map((p) => p.paymentLabel)
	});

	if (!hasValidPayments) {
		logger.error(
			'WS-TRACK-MY-OFFER: Global error - No valid payments found in PaymentObject data',
			{
				paymentArray,
				formattedPayments,
				allErrors: formattedPayments.every((p) => p.hasError)
			}
		);

		if (window.newrelic && window.newrelic.addPageAction) {
			window.newrelic.addPageAction('paymentGlobalError', {
				error: 'no_valid_payments',
				context: 'payment_object_update',
				source: 'payment-processing',
				totalPayments: formattedPayments.length,
				errorPayments: errorPayments.length,
				errorTypes: errorPayments
					.map((p) => p.paymentLabel)
					.join(',')
			});
		}

		return null;
	}

	const finalPayments = validPayments.map((payment, index) => ({
		...payment,
		isSelected: index === 0,
		firstItem: index === 0,
		lastItem: index === validPayments.length - 1
	}));

	logger.info(
		'WS-TRACK-MY-OFFER: Successfully processed PaymentObject data',
		{
			finalPaymentsCount: finalPayments.length,
			hasValidPayments,
			paymentSummary: finalPayments.map((p) => ({
				label: p.paymentLabel,
				hasError: p.hasError,
				payment: p.payment
			}))
		}
	);

	return finalPayments;
};
